import { useMemo } from 'react';

import { SellingMode } from '@src/core/buyerApiClient';
import { useWidgetId } from '@src/core/IdProvider';
import {
  adderResumeSelector,
  adderStateAndCurrentProductIdSelector,
  productSelector,
  selectedProductTariffHour,
  tariffsState,
} from '@src/stores/product';
import { addCartItemAddedSelector } from '@src/features/cart/store/cart';
import { useAppSelector, useTranslate } from '@src/core/hooks';
import { useBuildHourList } from '../features/product-booking/hooks/useBuildHourList';
import { useAllocationStatus } from './useAllocationStatus';
import { getSelectedTime } from '@src/domains/tariffs/core/tariffSelection';
import { getSchedulingFrom } from '@src/common/seatMaps';
import { areAllTariffsHoursUnavailable as areAllTariffsHoursUnavailableRule } from '../scheduleHours';
import { getSelectedTariffsPerType } from '@src/domains/tariffs';
import { cartErrorMessageKeys } from '@src/domains/cart/core/cartErrorMessageKey';

export const useAdderError = () => {
  const { i18n } = useTranslate();

  const { errors } = useAppSelector(addCartItemAddedSelector);
  const errorMessages = cartErrorMessageKeys(errors).map((key) => i18n(key));

  const widgetId = useWidgetId();
  const hoursList = useBuildHourList();
  const tariffsInformation = useAppSelector(tariffsState(widgetId));
  const selectedHour = useAppSelector(selectedProductTariffHour(widgetId));
  const { productId } = useAppSelector(adderStateAndCurrentProductIdSelector(widgetId));
  const product = useAppSelector(productSelector(productId))!;

  const { selectedTariffs } = useAppSelector(adderResumeSelector(widgetId, productId));

  const selectedTime = getSelectedTime(selectedHour);
  const { listOfTariffsPerType } = tariffsInformation;

  const selectedTariffsPerType = useMemo(() => getSelectedTariffsPerType(listOfTariffsPerType), [listOfTariffsPerType]);
  const scheduling = getSchedulingFrom(product, selectedTariffsPerType, selectedTime);

  const allocationStatus = useAllocationStatus({ scheduling, timeMinutes: selectedTime });

  const areAllTariffsHoursUnavailable = useMemo(
    () => areAllTariffsHoursUnavailableRule(hoursList, listOfTariffsPerType),
    [hoursList, listOfTariffsPerType],
  );

  // Only products with hours can have "all hours unavailable". For without-date and
  // date-only products there are no hours, so `hoursList` is empty — and the rule's vacuous
  // `[].every()` would otherwise flag the error as soon as a tariff is selected.
  const hasHours = product?.sellingMode === SellingMode.WithDateAndTime;

  if (hasHours && areAllTariffsHoursUnavailable && selectedTariffs.length > 0) {
    errorMessages.push(i18n('adder_hours_unavailable_error'));
  }

  if (allocationStatus.missingTariffs > 0 && selectedTariffs.length > 0) {
    errorMessages.push(
      i18n('adder_seats_missing_tariffs_unavailable_error', { missing: String(allocationStatus.missingTariffs) }),
    );
  }

  // The seat map cannot hold the whole selection. Saying how many places are short is the only
  // actionable part: the shopper either trims the selection or moves to another time.
  if (allocationStatus.missingCapacity > 0 && selectedTariffs.length > 0) {
    errorMessages.push(
      i18n('adder_error_seats_missing_capacity', { missing: String(allocationStatus.missingCapacity) }),
    );
  }

  return errorMessages;
};
